import { faImage } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers";
import { useCurrentEditor } from "@tiptap/react";
import classNames from "classnames";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Button, Modal } from "semantic-ui-react";
import * as yup from "yup";
import { coreUploadTempFile, FileUploadFinalizeResponse, FileUsage } from "../../../../../actions/coreUpload";
import { ControlledCheckbox, ControlledInput } from "../../../../ControlledForm";
import FileUploadInput from "../../../../file-manager/FileUploadInput";
import { useDragUpload } from "../hooks/useDragUpload";

type InsertMediaModalForm = {
  file: File;
  altText: string;
  isDecorative: boolean;
};
export type InsertMediaModalData = { type: MediaType; url: string; altText: string; isDecorative: boolean } | null;
type MediaType = "image" | "video";

export function ControlledRichTextEditorInsertMediaModal({
  data,
  onClose,
  onInsertImage,
  onInsertVideo,
  showIsDecorative = true,
  fileUsage,
}: {
  data: InsertMediaModalData;
  onClose: () => void;
  onInsertImage: (data: { url: string; altText: string; isDecorative: boolean; uuid?: string }) => void;
  onInsertVideo: (data: { url: string; uuid: string }) => void;
  showIsDecorative?: boolean;
  fileUsage: FileUsage;
}) {
  const dispatch = useDispatch();
  const { editor } = useCurrentEditor();
  const { control, handleSubmit, setValue, register } = useForm<InsertMediaModalForm>({
    defaultValues: {
      file: undefined,
      altText: data?.altText || "",
      isDecorative: data?.isDecorative || false,
    },
    resolver: yupResolver(
      yup.object().shape({
        file: yup.mixed(),
        altText: yup.string(),
        isDecorative: yup.boolean(),
      }),
    ),
  });
  register("file", { required: "Please select an image" });

  const [selectedMedia, setSelectedMedia] = useState<{ urlOrUUID: string; type: MediaType } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onUploadFiles = async (files: File[]) => {
    const file = files[0];
    if (file) {
      const fileIsImage = file.type.startsWith("image/");
      const fileIsVideo = file.type.startsWith("video/");

      // TODO: Remove this line after adding video support
      if (fileIsVideo) return;

      if (!fileIsImage && !fileIsVideo) return;

      setValue("file", file);

      if (fileIsImage) {
        const imageUrl = await imageFromFile(file);
        setSelectedMedia({
          urlOrUUID: imageUrl,
          type: "image",
        });
      }

      if (fileIsVideo) {
        const videoUrl = await videoFromFile(file);
        setSelectedMedia({
          urlOrUUID: videoUrl,
          type: "video",
        });
      }
    }
  };

  const { isDragActive, dragUploadProps } = useDragUpload({
    onUpload: onUploadFiles,
  });

  const onSubmit: SubmitHandler<InsertMediaModalForm> = async ({ file, altText, isDecorative }) => {
    // File didn't change, just use existing URL and update alt text/decorative flag
    if (data && !file) {
      onInsertImage({ url: data.url, altText, isDecorative });
      onClose();
      editor?.commands.focus();
      return;
    }

    const fileIsImage = file.type.startsWith("image/");
    const fileIsVideo = file.type.startsWith("video/");

    setIsLoading(true);
    const response = (await dispatch(
      coreUploadTempFile({
        id: `rich-text-editor-${file.name}`,
        file,
        usage: fileUsage,
      }),
    )) as any;
    const payload = response.payload as FileUploadFinalizeResponse;
    const { url, uuid } = payload;
    setIsLoading(false);

    if (fileIsImage) {
      onInsertImage({ url, altText, isDecorative, uuid });
    }

    if (fileIsVideo) {
      onInsertVideo({ url, uuid });
    }

    onClose();
    editor?.commands.focus();
  };

  const getFinalSelectedMedia = () => {
    if (selectedMedia) return selectedMedia;
    if (data) {
      return {
        urlOrUUID: data.url,
        type: data.type,
      };
    }
    return null;
  };

  const finalSelectedMedia = getFinalSelectedMedia();

  return (
    <Modal open={!!data} onClose={onClose} size='tiny'>
      {/* TODO: Uncomment this line after adding video support */}
      {/* <Modal.Header>Insert/Edit Media</Modal.Header> */}
      <Modal.Header>Insert/Edit Image</Modal.Header>
      <Modal.Content className='tw-space-y-3'>
        <FileUploadInput
          onChange={(files) => onUploadFiles(Array.from(files))}
          allowMultiUpload={false}
          // TODO: Uncomment this line after adding video support
          // accept='image/*,video/*'
          accept='image/*'
        >
          <div {...dragUploadProps}>
            {finalSelectedMedia?.urlOrUUID ? (
              <div className='tw-group tw-relative tw-flex tw-justify-center'>
                {finalSelectedMedia.type === "image" && (
                  <img
                    className={classNames(
                      "tw-max-h-64 tw-max-w-full tw-rounded tw-transition-all tw-duration-75 group-hover:tw-opacity-40 group-hover:tw-blur-sm",
                      {
                        "tw-opacity-40 tw-blur-sm": isDragActive,
                      },
                    )}
                    src={finalSelectedMedia.urlOrUUID}
                    alt='upload'
                  />
                )}
                {finalSelectedMedia.type === "video" && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    className={classNames(
                      "tw-max-h-64 tw-max-w-full tw-rounded tw-transition-all tw-duration-75 group-hover:tw-opacity-40 group-hover:tw-blur-sm",
                      {
                        "tw-opacity-40 tw-blur-sm": isDragActive,
                      },
                    )}
                    src={finalSelectedMedia.urlOrUUID}
                    autoPlay
                    muted
                    loop
                  />
                )}
                <div className='tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center'>
                  <div
                    className={classNames(
                      "tw-font-bold tw-text-text tw-opacity-0 tw-transition-all tw-duration-75 group-hover:tw-opacity-100",
                      {
                        "tw-opacity-100": isDragActive,
                      },
                    )}
                  >
                    Change Image
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={classNames(
                  "tw-border-1 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-border-dashed tw-border-border tw-p-6 tw-text-text-hint tw-transition-colors tw-duration-75 hover:tw-border-border-dark hover:tw-text-text-secondary",
                  {
                    "tw-border-border-dark tw-text-text-secondary": isDragActive,
                  },
                )}
              >
                <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2'>
                  <FontAwesomeIcon icon={faImage} />
                  Choose an image or drag it here
                </div>
              </div>
            )}
          </div>
        </FileUploadInput>
        {finalSelectedMedia?.type === "image" && (
          <div className='tw-space-y-3'>
            <div>
              <ControlledInput
                name={"altText"}
                control={control}
                label={"Alt Text"}
                subLabel='Add a description to be WCAG compliant'
                fluid
              />
            </div>
            {showIsDecorative && (
              <ControlledCheckbox name='isDecorative' control={control} label='Mark as Decorative' />
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isLoading || !finalSelectedMedia}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

async function imageFromFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      resolve(img.src);
    };
    img.onerror = (error) => {
      reject(error);
    };
  });
}

async function videoFromFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.onloadeddata = () => {
      resolve(video.src);
    };
    video.onerror = (error) => {
      reject(error);
    };
  });
}
